import * as React from "react";
import Head from 'next/head';
import { EventComponent } from "../../generated/apolloComponents";
import SpeakerCard from "./SpeakerCard";
import { Container, Row, Col, Card, CardImg, Badge, Alert } from "reactstrap";
import Calendar from 'react-feather/dist/icons/calendar';
import MapPin from 'react-feather/dist/icons/map-pin';
import Cast from 'react-feather/dist/icons/cast';
import ExternalLink from 'react-feather/dist/icons/external-link';
import Award from 'react-feather/dist/icons/award';
import Tag from 'react-feather/dist/icons/tag';
import OutboundLink from '../ui/OutboundLink';
import headTitle from "../ui/HeadTitle";
import SubscribeCard from "components/newsletter/SubscribeCard";
import { prettyLink } from "../ui/PrettyLink";
import EventCard from "components/event/EventCard";
import classNames from "classnames";
import Advertisment from "./Advertisment";

type EventDataProps = {
  name: string,
  date?: string,
  place?: string,
  googlePlaceId?: string,
  url?: string,
  topics: Array<string>,
  online: boolean,
  featured: boolean,
};

const EventData: React.FunctionComponent<EventDataProps> = ({ name, date, place, online, topics, url, featured }) => {
  return (<div className="py-3 my-3">
    {date && <div className="d-flex py-1">
        <div className="flex-shrink-1">
          <Calendar size={24} className="mr-2" />
        </div>
        <div className="w-100">
          <span className="d-block mb-1">{date}</span>
        </div>
    </div>}
    {online && <div className="d-flex py-1 text-success">
        <div className="flex-shrink-1">
          <Cast size={24} className="mr-2" />
        </div>
        <div className="w-100">
          <span className="d-block mb-1">Online</span>
        </div>
    </div>}
    {place && <div className="d-flex py-1">
        <div className="flex-shrink-1">
          <MapPin size={24} className="mr-2" />
        </div>
        <div className="w-100">
          <OutboundLink className="d-block mb-1 text-dark" eventLabel={`${name} - Maps`} to={'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(place)} rel="nofollow noopener" target="_blank">{place}</OutboundLink>
        </div>
    </div>}
    {url && <div className="d-flex py-1">
        <div className="flex-shrink-1">
          <ExternalLink size={24} className="mr-2" />
        </div>
        <div className="w-100 text-truncate">
          <OutboundLink className="d-block mb-1 text-dark" eventLabel={`${name} - Web`} to={url} rel="nofollow noopener" target="_blank">{prettyLink(url)}</OutboundLink>
        </div>
    </div>}
    {topics.length > 0 && <div className="d-flex py-1">
        <div className="flex-shrink-1">
          <Tag size={24} className="mr-2" />
        </div>
        <div className="w-100" style={{lineHeight: 1.8}}>
          {topics.map(topic => <Badge color="secondary" className="font-weight-normal mr-1" pill={true} style={{fontSize: '1rem'}} key={topic}>{topic}</Badge>)}
        </div>
    </div>}
    {featured && <div className="d-flex py-1">
        <div className="flex-shrink-1">
          <Award size={24} className="mr-2" />
        </div>
        <div className="w-100">
          <Badge className="featured-bg" pill={true} style={{fontSize: '1rem'}}>Featured Event</Badge>
        </div>
    </div>}
  </div>);
};

type Props = {
  slug: string,
}

const EventDetail: React.FunctionComponent<Props> = ({ slug }) => {
  return <EventComponent variables={{ slug, featuredEventsCount: 3 }}>
    {({ data, loading, error }) => {
      if (loading) return <div>Loading...</div>;
      if (error) return <div>Error: {error}</div>;
      const event = data!.event!;
      const featuredEvents = data!.featuredEvents;
      let partnerships = {};
      event.partners!.edges!.forEach(edge => {
        if (!(edge!.partnershipType!.name in partnerships)) {
          partnerships[edge!.partnershipType!.name] = [];
        }
        partnerships[edge!.partnershipType!.name].push(edge!.node);
      });
      let metaDescription = `${event.name} will take place`;
      if (event.date) {
        metaDescription += ` on ${event.date}`;
      }
      if (event.online) {
        metaDescription += ` online`;
      } else if (event.place) {
        metaDescription += ` at ${event.place.name}`;
      }
      metaDescription += `. Find out more about ${event.name} and discover the best artificial intelligence and machine learning event alternatives.`;
      return (<>
        <Head>
          <title>{headTitle(event.name)}</title>
          <meta key="og:title" property="og:title" content={headTitle(event.name)} />
          <meta key="og:url" property="og:url" content={`https://aiml.events/events/${slug}`} />
          {event.image && <>
              <meta key="og:image" property="og:image" content={`https://aiml.events${event.image.imageCard}`} />
              <meta key="og:image:width" property="og:image:width" content={`${event.image.imageCardWidth}`} />
              <meta key="og:image:height" property="og:image:height" content={`${event.image.imageCardHeight}`} />
            </>}
          <meta key="og:description" property="og:description" content={metaDescription} />
          <meta key="twitter:card" property="twitter:card" content="summary_large_image" />
          <meta key="twitter:title" property="twitter:title" content={headTitle(event.name)} />
          <meta key="twitter:description" property="twitter:description" content={metaDescription} />
          {event.image && <meta key="twitter:image" property="twitter:image" content={`https://aiml.events${event.image.image}`} />}
          <meta name="description" content={metaDescription} />
        </Head>
        <div className="bg-light">
          <Container className="pt-4">
            <Row>
              <Col lg={6}>
                <h1 className="text-primary">{event.name}</h1>
                {event.alternativeName && <h2 className="text-primary">{event.alternativeName}</h2>}
                {event.canceled && <Alert color="danger">Event Canceled</Alert>}
                <EventData name={event.name} date={event.date!} url={event.url!} place={event.place?.formattedAddress} featured={event.featured} topics={event.topics} online={event.online} />
                <section dangerouslySetInnerHTML={{__html: event.description}}></section>
                <SubscribeCard />
              </Col>
              <Col lg={6}>
                <Row>
                  <Col>
                    <Advertisment />
                  </Col>
                </Row>
                {event.youtubePlaylistIds.length > 0 && event.youtubePlaylistIds.map(playlistId => <Row key={playlistId!}>
                    <Col>
                      <div className="mb-3 embed-responsive embed-responsive-16by9">
                        <iframe className="embed-responsive-item" src={`https://www.youtube-nocookie.com/embed/videoseries?list=${playlistId}`} allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                      </div>
                    </Col>
                  </Row>)}
                <Row>
                  <Col>
                    {event.image && <img src={event.image.image!} style={{width: '100%'}} className="mb-3 rounded" alt={event.name} />}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <div className={classNames('card-sm-columns', {'card-sm-2-count card-lg-2-count': event.images!.edges.length > 3})}>
                      {event.images!.edges.map((edge, index) => (
                        <Card className="mb-3" key={index}>
                          <CardImg src={edge!.node!.image!} top width="100%" alt={edge!.node!.caption || event.name} className="rounded" />
                        </Card>
                        // <img src={edge!.node!.imagePreview!} alt={edge!.node!.alt || event.name} height="200" className="p-1 rounded" />
                      ))}
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Container>
        </div>

        {event.speakers!.edges.length > 0 && <div className="bg-light">
            <Container className="pt-4">
                <h2 className="text-center">Speakers</h2>
                <Row>
                {event.speakers!.edges.map((edge, index) => (
                  <Col md={6} lg={4} key={`speaker-${index}`}>
                    <SpeakerCard {...edge!.node!} className="pb-2" />
                  </Col>
                ))}
                </Row>
              </Container>
          </div>}

        {Object.keys(partnerships).map((partnershipType, keyIndex) => <div key={keyIndex}>
          <div className="bg-light">
            <Container className="bg-light pt-4"> </Container>
          </div>
          <Container>
            <h2 className="text-center pt-3 pb-4">{partnershipType}{partnerships[partnershipType].length > 1 && 's'}</h2>
            <div className="row justify-content-center align-items-center text-center">
            {partnerships[partnershipType].map(partner => <div className="col-4 col-md-2 mb-8" key={`partner-${partner.id}`}>
              <img className="u-clients" src={partner.image.imagePreview} alt={partner.name} />
            </div>)}
          </div>
        </Container>
        </div>)}
        { event.place?.googlePlaceId && <>
            <div className="bg-light">
                <Container className="bg-light pt-4"> </Container>
            </div>
            <h2 className="text-center">Location</h2>
            <div>
              <iframe src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyDOVlTn5NI6J_aQ2JerJ4bG1qWyP4rHPjs&q=place_id:${event.place.googlePlaceId}`} width="100%" height="500px" frameBorder="0" style={{border:0}}></iframe>
            </div>
          </>}
        {!event.featured && featuredEvents!.edges.length > 0 && <div className="bg-light">
          <div className="bg-light">
            <Container className="bg-light pt-4"> </Container>
          </div>
          <Container className="pt-4">
              <h2 className="text-center">See Also</h2>
              <Row>
                {featuredEvents!.edges.map((edge, index) => {
                  return <Col md={6} lg={4} className={classNames('mb-5', {'d-md-none d-lg-block': index==2})} key={edge!.node!.id}>
                      <EventCard {...edge!.node!} featured={false} />
                    </Col>
                })}
              </Row>
            </Container>
          </div>}
        <div className="bg-light">
          <Container className="bg-light pt-4"> </Container>
        </div>
        {!event.approximateDate && <script type="application/ld+json" dangerouslySetInnerHTML={{__html: JSON.stringify(
          {
            "@context": "https://schema.org",
            "@type": "Event",
            "name": event.name,
            "startDate": event.fromDate,
            "endDate": event.toDate,
            "location": event.place ? {
              "@type": "Place",
              "name": event.place.name,
              "address": {
                "@type": "PostalAddress",
                "streetAddress": event.place.formattedAddress,
                "addressLocality": event.place.locality ? event.place.locality : undefined,
                "addressCountry": event.place.country,
              }
            } : undefined,
            "eventAttendanceMode": event.online ? (event.place ? 'https://schema.org/MixedEventAttendanceMode' : 'https://schema.org/OnlineEventAttendanceMode') : 'https://schema.org/OfflineEventAttendanceMode',
            "image": event.image ? [`https://aiml.events${event.image.image}`] : [],
            "description": event.summary,
            "offers": {
              "@type": "Offer",
              "url": `https://aiml.events/events/${event.slug}`,
              "availability": event.isOver ? "http://schema.org/SoldOut" : "https://schema.org/InStock",
            },
            "performer": [event.speakers!.edges.map(edge => ({
              "@type": "Person",
              "name": edge!.node!.name,
              "image": edge!.node!.image ? `https://aiml.events${edge!.node!.image!.image}` : undefined,
            }))],
            "eventStatus": event.canceled ? "http://schema.org/EventCancelled" : undefined,
          }
        )}}></script>}
      </>);
    }}
  </EventComponent>
}

export default EventDetail
